import React, { useState } from "react";
import { MessageSquare, CheckCircle2, ChevronRight, X, AlertTriangle, MessageCircle, Mic, Info } from "lucide-react";

const criteriaConfig = {
  fluency: {
    label: "Fluency & Coherence",
    shortLabel: "Fluency",
    description: "Measures speech rate, continuous flow without excessive hesitation, use of connectives, and idea sequencing.",
  },
  lexical: {
    label: "Lexical Resource",
    shortLabel: "Lexical",
    description: "Measures vocabulary range, word choice precision, use of collocations/idioms, and effective paraphrasing.",
  },
  grammar: {
    label: "Grammatical Range & Accuracy",
    shortLabel: "Grammar",
    description: "Measures the variety of sentence structures (simple, compound, complex) and grammatical accuracy.",
  },
  pronunciation: {
    label: "Pronunciation",
    shortLabel: "Pronunciation",
    description: "Measures articulation clarity, word and sentence stress, rhythm, and natural intonation.",
  },
};

export default function SpeakingResultModal({ answers = {}, evaluationResult = null, recordingIds = {}, questions = null, onClose }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [expiredAudioInfo, setExpiredAudioInfo] = useState(null);

  const handleAudioPlay = (event) => {
    const currentAudio = event.currentTarget;
    document.querySelectorAll("[data-speaking-audio='true']").forEach((audioElement) => {
      if (audioElement !== currentAudio) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
    });
  };

  const overallBand = evaluationResult?.overall_band ?? 0;
  const overallFeedback = evaluationResult?.overall_feedback || "The AI evaluation has completed based on official IELTS speaking assessment standards.";
  const strengths = evaluationResult?.strengths || [];
  const areasToImprove = evaluationResult?.areas_to_improve || [];
  const criteriaData = evaluationResult?.criteria || {};

  const getBandLabel = (score) => {
    if (score >= 8.5) return "Expert Speaker";
    if (score >= 7.5) return "Very Good Speaker";
    if (score >= 6.5) return "Good Speaker";
    if (score >= 5.5) return "Competent Speaker";
    if (score >= 4.5) return "Modest Speaker";
    if (score > 0) return "Limited Speaker";
    return "Not Evaluated";
  };

  const flattenedQuestions = React.useMemo(() => {
    const list = [];
    if (Array.isArray(questions?.part1)) {
      questions.part1.forEach((q) => list.push({ ...q, partNumber: 1 }));
    }
    if (Array.isArray(questions?.part2)) {
      questions.part2.forEach((q) => list.push({ ...q, partNumber: 2 }));
    }
    if (Array.isArray(questions?.part3)) {
      questions.part3.forEach((q) => list.push({ ...q, partNumber: 3 }));
    }

    if (list.length > 0) return list;

    if (Array.isArray(evaluationResult?.recordings)) {
      evaluationResult.recordings.forEach((recording, idx) => {
        const question = recording.question || {};
        list.push({
          id: recording.question_id,
          part: recording.part || question.part,
          partNumber: recording.part || question.part,
          topic: question.topic,
          instruction: question.instruction || `Question ${idx + 1}`,
          cue_card_bullets: question.cue_card_bullets,
          order_number: question.order_number,
        });
      });
    }

    return list;
  }, [questions, evaluationResult]);

  const recordingsByQuestion = React.useMemo(() => {
    const map = {};

    if (Array.isArray(evaluationResult?.recordings)) {
      evaluationResult.recordings.forEach((recording) => {
        if (recording.question_id) {
          map[recording.question_id] = recording;
        }
      });
    }

    return map;
  }, [evaluationResult]);

  return (
    <div className="fixed inset-0 z-50 bg-[#000000aa] backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 md:p-6">
      <div className="relative bg-white rounded-2xl w-full max-w-5xl shadow-2xl flex flex-col max-h-full overflow-hidden transition duration-300">

        {/* Header */}
        <div className="bg-gradient-to-r from-[#1e3e7e] to-[#a51c30] p-5 text-white flex justify-between items-center relative flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-yellow-400 font-bold border border-white/20 font-poppins">
              ST
            </div>
            <div>
              <h1 className="font-anton text-xl sm:text-2xl tracking-wide uppercase">
                Speaking Assessment Report
              </h1>
              <p className="text-xs text-blue-100 font-poppins">
                Official criteria scoring & actionable feedback
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-black/20 hover:bg-black/30 flex items-center justify-center transition border border-white/10 cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tab Navigasi */}
        <div
          className="border-b border-gray-200 bg-gray-50 flex overflow-x-auto no-scrollbar flex-shrink-0"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          <style>{`
            .no-scrollbar::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          {[
            { id: "overview", label: "Overview" },
            { id: "breakdown", label: "Score Breakdown" },
            { id: "feedback", label: "Detail Feedback" },
            { id: "recordings", label: "Audio Playback" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 sm:px-6 py-3 font-poppins font-semibold text-xs sm:text-sm transition border-b-2 whitespace-nowrap cursor-pointer ${activeTab === tab.id
                ? "border-[#1e3e7e] text-[#1e3e7e] bg-white"
                : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Konten Utama */}
        <div className="p-3 sm:p-4 overflow-y-auto flex-1 min-h-0 bg-gray-50/50">

          {/* Tab Overview */}
          {activeTab === "overview" && (
            <div className="space-y-6">

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div className="md:col-span-1 flex flex-col items-center justify-center text-center p-4 border-b md:border-b-0 md:border-r border-gray-100">
                  <span className="text-gray-500 font-poppins text-xs font-bold uppercase tracking-wider">Overall Band Score</span>
                  <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full border-[10px] border-[#1e3e7e] flex items-center justify-center text-4xl sm:text-5xl font-black text-[#1e3e7e] my-3 shadow-inner bg-blue-50/40 font-poppins">
                    {overallBand}
                  </div>
                  <span className="px-3 py-1 rounded-full bg-blue-100 text-[#1e3e7e] text-xs font-bold font-poppins">
                    {getBandLabel(overallBand)}
                  </span>
                </div>

                <div className="md:col-span-2 space-y-4 flex flex-col justify-center">
                  <h3 className="font-anton text-lg sm:text-xl text-[#1e3e7e]">
                    Congratulations! Your speaking simulation is complete.
                  </h3>
                  <p className="font-poppins text-sm text-gray-700 leading-relaxed">
                    {overallFeedback}
                  </p>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-poppins pt-2">
                    {Object.entries(criteriaConfig).map(([key, config]) => {
                      const score = criteriaData[key]?.band ?? "-";
                      return (
                        <div key={key} className="p-2.5 bg-blue-50/50 rounded-xl border border-blue-100 text-center">
                          <span className="block text-gray-500 text-[10px] uppercase font-semibold">{config.shortLabel}</span>
                          <strong className="text-[#1e3e7e] text-base font-black">{score}</strong>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Skala Band Score IELTS */}
              <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                <h4 className="font-anton text-sm text-gray-500 uppercase mb-4 tracking-wider text-center">
                  IELTS Band Score Scale
                </h4>
                <div className="grid grid-cols-9 gap-1.5 sm:gap-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => {
                    const isSelected = i === Math.floor(overallBand) || (overallBand % 1 !== 0 && i === Math.ceil(overallBand));
                    return (
                      <div
                        key={i}
                        className={`flex flex-col items-center justify-center p-2 rounded-xl transition ${isSelected
                          ? "bg-gradient-to-br from-[#1e3e7e] to-blue-800 text-white shadow-md scale-105"
                          : "bg-gray-100 text-gray-600 border border-gray-200/50"
                          }`}
                      >
                        <span className="text-base sm:text-lg font-black font-poppins">{i}</span>
                        <span className="text-[8px] sm:text-[9px] font-semibold opacity-80 uppercase tracking-tighter hidden sm:block font-poppins">
                          {i === 9 ? "Expert" : i === 7 ? "Good" : i === 5 ? "Modest" : ""}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Tab Score Breakdown */}
          {activeTab === "breakdown" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {Object.entries(criteriaConfig).map(([key, config]) => {
                const item = criteriaData[key] || {};
                const score = item.band ?? 0;
                const feedbackText = item.feedback || "No specific feedback available for this criterion.";

                const colorClass = score >= 7.0 ? "bg-green-500" : score >= 5.5 ? "bg-yellow-400" : "bg-red-500";

                return (
                  <div key={key} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-start gap-2 mb-2">
                        <h4 className="font-anton text-md text-[#1e3e7e]">{config.label}</h4>
                        <span className="font-black font-poppins text-lg text-gray-800">{score} / 9.0</span>
                      </div>

                      <p className="text-xs text-gray-500 font-poppins leading-relaxed mb-4">
                        {config.description}
                      </p>

                      <div className="w-full bg-gray-100 h-3 rounded-full overflow-hidden mb-4">
                        <div className={`h-full ${colorClass} transition-all duration-500`} style={{ width: `${(score / 9) * 100}%` }}></div>
                      </div>

                      <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 text-xs font-poppins">
                        <span className="block text-[#1e3e7e] font-bold text-xs uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                          <MessageSquare size={14} /> AI Evaluator Feedback
                        </span>
                        <p className="text-gray-700 leading-relaxed text-xs">
                          {feedbackText}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Tab Detail Feedback */}
          {activeTab === "feedback" && (
            <div className="space-y-6">

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                {/* Strengths */}
                <div className="bg-white p-5 rounded-2xl border border-green-200/50 shadow-sm">
                  <div className="flex items-center gap-2.5 mb-4 border-b border-green-50 pb-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                      <CheckCircle2 size={18} />
                    </div>
                    <h3 className="font-anton text-md text-[#1e3e7e] tracking-wide uppercase">
                      Your Strengths
                    </h3>
                  </div>
                  {strengths.length > 0 ? (
                    <ul className="space-y-3 text-xs sm:text-sm font-poppins text-gray-700">
                      {strengths.map((str, idx) => (
                        <li key={idx} className="flex gap-2 items-start leading-relaxed">
                          <ChevronRight size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                          <span>{str}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs font-poppins text-gray-500 italic">No specific strengths data available.</p>
                  )}
                </div>

                {/* Areas to Improve */}
                <div className="bg-white p-5 rounded-2xl border border-yellow-200/50 shadow-sm">
                  <div className="flex items-center gap-2.5 mb-4 border-b border-yellow-50 pb-3">
                    <div className="w-8 h-8 rounded-full bg-yellow-100 text-yellow-600 flex items-center justify-center">
                      <AlertTriangle size={18} />
                    </div>
                    <h3 className="font-anton text-md text-[#1e3e7e] tracking-wide uppercase">
                      Areas to Improve
                    </h3>
                  </div>
                  {areasToImprove.length > 0 ? (
                    <ul className="space-y-3 text-xs sm:text-sm font-poppins text-gray-700">
                      {areasToImprove.map((imp, idx) => (
                        <li key={idx} className="flex gap-2 items-start leading-relaxed">
                          <ChevronRight size={16} className="text-yellow-500 flex-shrink-0 mt-0.5" />
                          <span>{imp}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs font-poppins text-gray-500 italic">No specific improvement areas available.</p>
                  )}
                </div>
              </div>

            </div>
          )}

          {/* Tab Audio Playback */}
          {activeTab === "recordings" && (
            <div className="space-y-5">
              {flattenedQuestions.length > 0 ? (
                flattenedQuestions.map((q, idx) => {
                  const questionAnswer = answers[q.id];
                  const recording = recordingsByQuestion[q.id];
                  const isExpiredRecording = recording?.is_expired === true;

                  return (
                    <div key={q.id || idx} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm space-y-4">
                      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 border-b border-gray-50 pb-3">
                        <div className="flex-1 min-w-0">
                          <span className="px-2.5 py-0.5 rounded-md bg-[#1e3e7e]/10 text-[#1e3e7e] text-[10px] font-bold font-poppins uppercase tracking-wider">
                            Part {q.partNumber || q.part} {q.topic ? `• ${q.topic}` : ""}
                          </span>
                          <h4 className="font-poppins font-bold text-gray-800 text-sm sm:text-base mt-1.5 leading-snug">
                            {q.instruction || q.topic || `Question ${idx + 1}`}
                          </h4>
                          {q.cue_card_bullets && Array.isArray(q.cue_card_bullets) && q.cue_card_bullets.length > 0 && (
                            <ul className="mt-2 text-xs text-gray-600 list-disc list-inside space-y-0.5 font-poppins">
                              {q.cue_card_bullets.map((b, bIdx) => (
                                <li key={bIdx}>{b}</li>
                              ))}
                            </ul>
                          )}
                        </div>

                        {/* Audio Playback Controls */}
                        {questionAnswer?.audioUrl || questionAnswer?.blob ? (
                          <div className="flex flex-col sm:items-end gap-1.5 w-full sm:w-auto">
                            <audio
                              data-speaking-audio="true"
                              controls
                              controlsList="nodownload"
                              src={questionAnswer.audioUrl || (questionAnswer.blob ? URL.createObjectURL(questionAnswer.blob) : "")}
                              className="w-full sm:w-64 h-9 rounded-lg"
                              preload="none"
                              onPlay={handleAudioPlay}
                            >
                              Browser Anda tidak mendukung pemutar audio ini.
                            </audio>
                          </div>
                        ) : isExpiredRecording ? (
                          <button
                            type="button"
                            onClick={() => setExpiredAudioInfo({
                              question: q.instruction || q.topic || `Question ${idx + 1}`,
                            })}
                            className="inline-flex items-center gap-2 text-xs font-poppins text-amber-700 bg-amber-50 px-3 py-2 rounded-lg border border-amber-200 w-fit hover:bg-amber-100 transition"
                          >
                            <Info size={15} />
                            Rekaman kedaluwarsa
                          </button>
                        ) : (
                          <span className="text-xs font-poppins text-gray-400 bg-gray-100 px-3 py-1.5 rounded-full border border-gray-200 w-fit">
                            Audio Recorded on Server
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="bg-white p-8 rounded-2xl text-center border border-gray-100">
                  <div className="w-12 h-12 rounded-full bg-blue-50 text-[#1e3e7e] flex items-center justify-center mx-auto mb-3">
                    <Mic size={24} />
                  </div>
                  <h4 className="font-poppins font-bold text-gray-800 text-sm mb-1">
                    All Audio Recordings Uploaded
                  </h4>
                  <p className="text-xs text-gray-500 font-poppins max-w-md mx-auto">
                    All your recorded speaking answers have been uploaded and processed by the AI evaluation system.
                  </p>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="bg-white border-t border-gray-100 p-4 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-xs text-gray-500 font-poppins text-center sm:text-left">
            Want more in-depth personal feedback? Reach out to our mentor team.
          </div>
          <div className="flex gap-3 w-full sm:w-auto">
            <button
              onClick={onClose}
              className="flex-1 sm:flex-none px-6 py-2.5 border border-gray-300 text-gray-700 font-poppins font-semibold text-sm rounded-full hover:bg-gray-100 transition cursor-pointer"
            >
              Close
            </button>
            <a
              href="https://wa.me/6281234567890?text=Halo%20ScholarsToday%2C%20saya%20baru%20saja%20menyelesaikan%20simulasi%20IELTS%20Speaking%20dan%20ingin%20berdiskusi%20dengan%20mentor."
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 sm:flex-none px-6 py-2.5 bg-green-500 text-white font-poppins font-bold text-sm rounded-full hover:bg-green-600 transition flex items-center justify-center gap-2 shadow-md cursor-pointer"
            >
              <MessageCircle size={16} /> Ask Mentor
            </a>
          </div>
        </div>

        {expiredAudioInfo && (
          <div className="absolute inset-0 z-10 bg-black/40 backdrop-blur-[1px] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-sm p-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center flex-shrink-0">
                  <Info size={19} />
                </div>
                <div className="min-w-0">
                  <h3 className="font-poppins font-bold text-gray-900 text-sm">
                    Rekaman sudah tidak tersedia
                  </h3>
                  <p className="font-poppins text-xs text-gray-600 leading-relaxed mt-2">
                    Audio speaking hanya disimpan selama 14 hari setelah direkam. Setelah melewati batas tersebut, file dihapus dari storage sehingga tidak bisa diputar lagi.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setExpiredAudioInfo(null)}
                className="mt-5 w-full px-4 py-2 rounded-lg bg-[#1e3e7e] text-white text-sm font-semibold font-poppins hover:bg-blue-800 transition"
              >
                Mengerti
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
